import { Link } from 'react-router-dom'
import LegalLayout from './LegalLayout'
import { LEGAL_ENTITY as ED } from './legalEntity'

/** Terms of use — they bind every user of the portal, including those who never subscribed. */
export default function CGU() {
  return (
    <LegalLayout
      title="Conditions générales d’utilisation"
      description="Règles d’accès et d’utilisation du portail Espace Client : comptes, rôles, disponibilité, obligations et responsabilités."
    >
      <h2>1. Objet</h2>
      <p>
        Les présentes conditions générales d’utilisation régissent l’accès au portail {ED.service}{' '}
        et son utilisation. Elles s’appliquent à tout utilisateur, qu’il soit l’entreprise ayant
        souscrit un abonnement, un membre de ses équipes ou l’un de ses clients invité à consulter
        son espace. Les conditions financières de la souscription figurent dans les{' '}
        <Link to="/cgv">conditions générales de vente</Link>.
      </p>
      <p>
        L’utilisation du portail vaut acceptation des présentes conditions. L’utilisateur qui ne les
        accepte pas doit renoncer à accéder au service.
      </p>

      <h2>2. Description du service</h2>
      <p>
        {ED.service} est un portail en ligne qui restitue à chaque utilisateur, dans un espace
        sécurisé et aux couleurs de l’entreprise qui l’a mis à sa disposition, les informations
        issues du système de gestion de cette entreprise : factures, commandes, devis, expéditions,
        documents, catalogue et demandes de support. Les fonctionnalités réellement accessibles
        dépendent des modules activés par l’entreprise et de la formule souscrite.
      </p>
      <p>
        {ED.societe} n’est pas partie à la relation commerciale entre l’entreprise utilisatrice et
        ses propres clients. Le portail est un outil de consultation et d’échange ; il ne se
        substitue pas aux documents contractuels émis par l’entreprise.
      </p>

      <h2>3. Accès et comptes</h2>
      <p>
        L’accès nécessite un compte nominatif, créé par un administrateur de l’entreprise
        utilisatrice, par invitation ou après validation d’une demande d’inscription. Les
        identifiants sont personnels et confidentiels : l’utilisateur est responsable de leur
        conservation et de toute action réalisée depuis son compte.
      </p>
      <p>
        Toute utilisation suspecte doit être signalée sans délai à {ED.email} ou à l’administrateur
        de l’entreprise concernée, afin que l’accès puisse être suspendu.
      </p>

      <h2>4. Rôles et périmètre des accès</h2>
      <ul>
        <li>
          <strong>Utilisateur client</strong> : accède aux seules données de l’entreprise cliente à
          laquelle son compte est rattaché.
        </li>
        <li>
          <strong>Administrateur</strong> : gère les clients et les utilisateurs du périmètre de son
          entreprise, et peut consulter les espaces correspondants pour les besoins de l’assistance.
        </li>
        <li>
          <strong>Administrateur de la plateforme</strong> : assure l’exploitation technique du
          service pour l’ensemble des entreprises utilisatrices.
        </li>
      </ul>
      <p>
        Chaque utilisateur s’engage à n’accéder qu’aux données relevant de son périmètre et à ne pas
        tenter d’en étendre la portée.
      </p>

      <h2>5. Obligations de l’utilisateur</h2>
      <p>L’utilisateur s’interdit notamment :</p>
      <ul>
        <li>
          de tenter d’accéder à des données ou fonctionnalités auxquelles il n’a pas droit, de
          contourner les contrôles d’accès ou d’exploiter une faille de sécurité ;
        </li>
        <li>
          de perturber le fonctionnement du service, notamment par un volume anormal de requêtes ou
          par l’usage d’outils automatisés non autorisés ;
        </li>
        <li>
          de déposer des contenus illicites, ou des fichiers contenant un code malveillant, dans les
          espaces de dépôt de documents ;
        </li>
        <li>
          d’extraire ou de réutiliser de manière systématique les données du portail à des fins
          étrangères à la relation avec l’entreprise concernée.
        </li>
      </ul>

      <h2>6. Disponibilité et maintenance</h2>
      <p>
        {ED.societe} s’efforce d’assurer la disponibilité du portail sans interruption, sans y être
        tenue par une obligation de résultat. L’accès peut être suspendu pour maintenance, mise à
        jour ou raison de sécurité. Les interventions programmées sont, dans la mesure du possible,
        annoncées à l’avance et réalisées en dehors des heures ouvrées.
      </p>
      <p>
        Le portail dépend de la disponibilité du système de gestion de l’entreprise utilisatrice et
        du réseau : une indisponibilité de ces éléments peut empêcher l’affichage des données sans
        que le service lui-même soit en cause.
      </p>

      <h2>7. Propriété intellectuelle</h2>
      <p>
        Le portail, ses interfaces, son code et sa documentation demeurent la propriété exclusive de{' '}
        {ED.societe}. L’utilisateur bénéficie d’un droit d’usage personnel, non exclusif et non
        cessible, limité à la durée de son accès. Toute reproduction, décompilation ou tentative de
        recréation du service est interdite, sauf dans les limites impératives de l’article L122-6-1
        du code de la propriété intellectuelle.
      </p>
      <p>
        Les données et documents déposés ou affichés dans le portail restent la propriété de
        l’entreprise utilisatrice ou de ses clients.
      </p>

      <h2>8. Données personnelles</h2>
      <p>
        Les traitements de données personnelles réalisés dans le cadre du service sont décrits dans
        la <Link to="/confidentialite">politique de confidentialité</Link>, qui fait partie
        intégrante des présentes conditions.
      </p>

      <h2>9. Responsabilité</h2>
      <p>
        {ED.societe} est tenue d’une obligation de moyens. Elle ne répond pas de l’exactitude des
        données métier transmises par le système de gestion de l’entreprise utilisatrice, ni des
        conséquences d’une utilisation du portail non conforme aux présentes conditions, ni d’une
        divulgation d’identifiants imputable à l’utilisateur.
      </p>

      <h2>10. Suspension et résiliation de l’accès</h2>
      <p>
        En cas de manquement grave aux présentes conditions, notamment d’atteinte à la sécurité du
        service ou aux données d’autres utilisateurs, l’accès peut être suspendu sans préavis, puis
        résilié après information de l’entreprise utilisatrice. L’accès prend également fin
        automatiquement au terme de l’abonnement de cette entreprise.
      </p>

      <h2>11. Modification des conditions</h2>
      <p>
        Les présentes conditions peuvent être modifiées pour tenir compte des évolutions du service
        ou de la réglementation. La version applicable est celle publiée sur cette page ; la date de
        dernière mise à jour y figure. Les modifications substantielles sont portées à la
        connaissance des utilisateurs par e-mail ou par une information dans le portail.
      </p>

      <h2>12. Droit applicable et juridiction</h2>
      <p>
        Les présentes conditions sont soumises au droit français. À défaut de résolution amiable,
        tout litige relève de la compétence exclusive du tribunal de commerce de {ED.rcsVille}.
      </p>
    </LegalLayout>
  )
}
